/**
 * Formats a number as a currency string
 * @param amount - The amount to format
 * @returns Formatted currency string (e.g., "₦1,234")
 */
export const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return '';
  
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Formats a number with thousand separators for input display
 * @param value - The value to format (string or number)
 * @param preserveDecimal - Whether to preserve decimal point if user is typing
 * @returns Formatted string with thousand separators (e.g., "1,234.56")
 */
export const formatNumberWithSeparators = (value: string | number | null | undefined, preserveDecimal = false): string => {
  if (value === null || value === undefined || value === '') return '';
  
  const strValue = String(value);
  
  // If user is typing a decimal, preserve it
  if (preserveDecimal && strValue.endsWith('.')) {
    const numValue = parseFloat(strValue);
    if (!isNaN(numValue)) {
      return numValue.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: true
      }) + '.';
    }
    return strValue;
  }
  
  // Format the number with thousand separators
  const numValue = parseFloat(strValue);
  if (!isNaN(numValue)) {
    const parts = strValue.split('.');
    const integerPart = parts[0] || '0';
    const decimalPart = parts[1];
    
    // Format integer part with thousand separators
    const formattedInteger = parseInt(integerPart, 10).toLocaleString('en-US', {
      useGrouping: true
    });
    
    // Combine with decimal part if present
    if (decimalPart !== undefined) {
      return `${formattedInteger}.${decimalPart}`;
    }
    
    return formattedInteger;
  }
  
  return strValue;
};

/**
 * Removes thousand separators from a formatted number string
 * @param value - The formatted string (e.g., "1,234.56")
 * @returns Clean numeric string (e.g., "1234.56")
 */
export const removeNumberSeparators = (value: string): string => {
  return value.replace(/,/g, '');
};

/**
 * Nigerian states list for state extraction
 */
const NIGERIAN_STATES = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
  'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'Gombe', 'Imo',
  'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos',
  'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers',
  'Sokoto', 'Taraba', 'Yobe', 'Zamfara', 'FCT'
];

/**
 * Extracts Nigerian state name from an address string
 * @param address - The address string to parse
 * @returns The state name if found, undefined otherwise
 */
export const extractStateFromAddress = (address: string | undefined): string | undefined => {
  if (!address) return undefined;

  // Normalize the address to handle case-insensitive matching
  const normalizedAddress = address.toLowerCase();

  // Try to find a state name in the address
  for (const state of NIGERIAN_STATES) {
    const normalizedState = state.toLowerCase();

    // Match whole word to avoid partial matches (e.g., "Delta" in "Ude Delta Street")
    const regex = new RegExp(`\\b${normalizedState}\\b`, 'i');
    if (regex.test(normalizedAddress)) {
      return state;
    }
  }

  return undefined;
};
